/**
 * „Salda klientów" (T-C2's left card): every client of the active roster with where they stand,
 * the most owed first (`model/creditLedger`), and under them since when the trainer bills in
 * credits. A row is a button that opens that client's ledger beside the list; the open one says so
 * (`aria-current`) and wears the canon's sunken ground. Drawn once the list has answered — its
 * height is the list's.
 */

import { useTranslation } from 'react-i18next';

import { displayInitials } from '~/shared/lib/displayInitials';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { standingPhrase } from '~/features/credit-ledger';

import { type ClientBalanceRow } from '../model/creditLedger';
import frame from './bookingTab.module.css';
import styles from './LedgerTab.module.css';

interface BalancesCardProps {
  readonly rows: readonly ClientBalanceRow[];
  readonly openId: number | null;
  readonly since: string | null;
  readonly nameOf: (row: ClientBalanceRow) => string;
  readonly onOpen: (customerId: number) => void;
}

export function BalancesCard({ rows, openId, since, nameOf, onOpen }: BalancesCardProps) {
  const { t } = useTranslation();
  return (
    <section
      className={`${String(frame.card)} ${String(styles.balances)}`}
      aria-labelledby="booking-balances-title"
    >
      <div className={styles.balancesHead}>
        <h2 id="booking-balances-title" className={frame.cardTitle}>
          {t('web:booking.credits.list.title')}
        </h2>
        <span className={styles.quiet}>{t('web:booking.credits.list.order')}</span>
      </div>
      <div className={styles.columns} aria-hidden="true">
        <span>{t('web:booking.credits.list.client')}</span>
        <span>{t('web:booking.credits.list.balance')}</span>
      </div>
      <ul className={styles.balanceList}>
        {rows.map((row) => {
          const name = nameOf(row);
          const phrase = standingPhrase(row.standing, t);
          const open = row.customerId === openId;
          return (
            <li key={row.customerId}>
              <button
                type="button"
                className={`${String(styles.balanceRow)} ${String(styles.balanceButton)} ${open ? String(styles.balanceOpen) : ''}`}
                aria-current={open ? true : undefined}
                aria-label={t('web:booking.credits.list.selectedA11y', {
                  name,
                  standing: phrase,
                })}
                onClick={() => {
                  onOpen(row.customerId);
                }}
              >
                <AvatarChip initials={displayInitials(name)} avatarUrl={null} />
                <span className={styles.balanceName}>{name}</span>
                <span className={styles.balancePhrase}>{phrase}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {since === null ? null : <p className={styles.balancesFoot}>{since}</p>}
    </section>
  );
}
